import type { JudicialExtraction } from "@/mocks/judicialRequirements";

interface IAExtractionPanelProps {
  extraction: JudicialExtraction;
}

export default function IAExtractionPanel({ extraction }: IAExtractionPanelProps) {
  const data = extraction.extractedJson;

  return (
    <div className="bg-white border border-background-200/70 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-background-200/50 bg-purple-50/50">
        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
          <i className="ri-robot-2-line text-purple-500 text-base" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground-900">Análisis IA del documento</p>
          <p className="text-[11px] text-foreground-500">
            {extraction.validatedBy
              ? `Validado por ${extraction.validatedByName} el ${new Date(extraction.validatedAt!).toLocaleDateString("es-CL")}`
              : "Pendiente de validación humana"}
          </p>
        </div>
        {extraction.validatedBy && (
          <span className="ml-auto flex items-center gap-1 text-[11px] text-emerald-600 font-medium bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
            <i className="ri-check-line" /> Validado
          </span>
        )}
      </div>

      {/* Extracted Data */}
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Tribunal" value={data.tribunal} icon="ri-building-line" />
        <Field label="RIT" value={data.rit} icon="ri-scales-3-line" />
        <Field label="RUC" value={data.ruc} icon="ri-file-code-line" />
        <Field label="Número de Causa" value={data.numeroCausa} icon="ri-hashtag" />
        <Field label="Estudiante" value={data.estudiante} icon="ri-user-line" highlight={data.estudiante === "no informado"} />
        <Field label="Curso" value={data.curso} icon="ri-group-line" />
        <Field label="Fecha de Recepción" value={data.fechaRecepcion} icon="ri-calendar-line" />
        <Field label="Fecha Límite" value={data.fechaLimite} icon="ri-alarm-line" urgent />
        <Field label="Nivel de Urgencia" value={data.nivelUrgencia} icon="ri-alert-line" highlight />
      </div>

      {/* Informes Solicitados */}
      <div className="px-5 pb-3">
        <h4 className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-2">Informes Solicitados</h4>
        <div className="flex flex-wrap gap-1.5">
          {data.informesSolicitados.map((inf, i) => (
            <span key={i} className="text-xs bg-secondary-50 text-secondary-700 px-2.5 py-1 rounded-full border border-secondary-200">
              {inf}
            </span>
          ))}
        </div>
      </div>

      {/* Antecedentes Requeridos */}
      <div className="px-5 pb-3">
        <h4 className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-2">Antecedentes Requeridos</h4>
        <ul className="space-y-1">
          {data.antecedentesRequeridos.map((ant, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-foreground-700">
              <span className="text-accent-400 mt-0.5 flex-shrink-0"><i className="ri-checkbox-blank-circle-fill text-[6px]" /></span>
              {ant}
            </li>
          ))}
        </ul>
      </div>

      {/* Acciones Recomendadas */}
      <div className="px-5 pb-3">
        <h4 className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-2">Acciones Recomendadas</h4>
        <ol className="space-y-1 list-decimal list-inside">
          {data.accionesRecomendadas.map((acc, i) => (
            <li key={i} className="text-xs text-foreground-700">{acc}</li>
          ))}
        </ol>
      </div>

      {/* Advertencias */}
      {data.advertencias.length > 0 && (
        <div className="px-5 pb-4">
          <h4 className="text-[11px] font-bold text-red-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <i className="ri-error-warning-line text-sm" /> Advertencias
          </h4>
          <div className="space-y-1.5">
            {data.advertencias.map((adv, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-red-700 bg-red-50 px-3 py-2 rounded-lg border border-red-200">
                <span className="text-red-400 mt-0.5 flex-shrink-0"><i className="ri-error-warning-fill text-sm" /></span>
                {adv}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  icon,
  highlight,
  urgent,
}: {
  label: string;
  value: string;
  icon: string;
  highlight?: boolean;
  urgent?: boolean;
}) {
  const isMissing = value === "no informado" || value === "";

  return (
    <div className="flex items-start gap-2.5">
      <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
        urgent ? "text-red-500" : "text-foreground-400"
      }`}>
        <i className={`${icon} text-sm`} />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] text-foreground-400 font-medium uppercase tracking-wider">{label}</p>
        <p className={`text-sm font-medium ${
          isMissing
            ? "text-foreground-300 italic"
            : highlight && urgent
              ? "text-red-600"
              : highlight
                ? "text-foreground-800"
                : "text-foreground-800"
        }`}>
          {isMissing ? "No informado en el documento" : value}
        </p>
      </div>
    </div>
  );
}